import { supabaseUrl, supabaseKey } from '../lib/supabase.js';

export class SyncError extends Error {
  constructor(message, { status, code } = {}) {
    super(message);
    this.name = 'SyncError';
    this.status = status;
    this.code = code;
  }
}

function wrap(error) {
  return new SyncError(error.message ?? 'Sync request failed', {
    status: error.status,
    code: error.code,
  });
}

/**
 * Call an RPC with the access token captured at the start of a sync.
 * The shared client would otherwise send whatever session is current, so a
 * sign-out or account switch mid-request could write one person's cards
 * into another person's account.
 */
async function rpcAs(accessToken, name, args) {
  if (!accessToken) throw new SyncError('Not signed in');
  let res;
  try {
    res = await fetch(`${supabaseUrl}/rest/v1/rpc/${name}`, {
      method: 'POST',
      headers: {
        apikey: supabaseKey,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(args),
    });
  } catch (e) {
    throw new SyncError(e.message ?? 'Sync request failed');
  }
  const text = await res.text();
  let body = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { message: text };
    }
  }
  if (!res.ok) {
    throw new SyncError(body?.message ?? res.statusText, { status: res.status, code: body?.code });
  }
  return body;
}

/** Supabase-backed transport. Interface: auth, push, pull, summary, subscribe. */
export function createSupabaseTransport(supabase) {
  return {
    /** The account this sync run is allowed to touch. */
    async auth() {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw wrap(error);
      const session = data.session;
      if (!session?.access_token || !session.user?.id) return null;
      return { userId: session.user.id, accessToken: session.access_token };
    },
    async push(changes, auth) {
      return rpcAs(auth?.accessToken, 'push_changes', { changes });
    },
    async pull(cursor, limit, auth) {
      const data = await rpcAs(auth?.accessToken, 'pull_changes', {
        p_tx: cursor.tx,
        p_seq: cursor.seq,
        p_limit: limit,
      });
      return (data ?? []).map((r) => ({ ...r, tx: Number(r.tx), seq: Number(r.seq) }));
    },
    async summary(auth) {
      return (await rpcAs(auth?.accessToken, 'sync_summary', {})) ?? {};
    },
    /** Realtime nudge: calls onChange (debounced by caller) whenever a row changes. */
    subscribe(userId, onChange) {
      const channel = supabase
        .channel(`sync:${userId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'sync_signals', filter: `user_id=eq.${userId}` },
          () => onChange(),
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') onChange();
        });
      return () => {
        supabase.removeChannel(channel);
      };
    },
  };
}
